import {
    useSyncExternalStore,
    type ReactNode,
    useContext,
    useEffect,
    Fragment
} from "react";
import {
    NCoreUIKitToast
} from "../core/hooks";
import {
    type SnackBarContextType,
    type SnackBarDataType
} from "../types/snackBar";
import NCoreContext, {
    type ConfigType
} from "ncore-context";
import {
    context as portalContext,
    SNACK_BAR_PORTAL_NAME
} from "../helpers/portalize/context";
import {
    warnSnackBarDeprecated,
    getSnackBarToastProps
} from "../components/snackBar/util";
import {
    isDevelopment,
    warnOnce
} from "../utils/development";
import {
    uuid
} from "../utils";

const getIsSnackBarLayerUsed = () => portalContext.getPortals().some((portal) => portal.name === SNACK_BAR_PORTAL_NAME);

const getIsSnackBarLayerUsedOnServer = () => false;

const SnackBarLayerWarning = () => {
    const isSnackBarLayerUsed = useSyncExternalStore(portalContext.subscribe, getIsSnackBarLayerUsed, getIsSnackBarLayerUsedOnServer);

    useEffect(() => {
        if(!isSnackBarLayerUsed) {
            return;
        }

        warnOnce({
            message: "The \"snack-bar-system\" portal layer is deprecated and will be removed in the next major version. Use the \"toast-system\" layer instead."
        });
    }, [
        isSnackBarLayerUsed
    ]);

    return null;
};

class NCoreUIKitSnackBar extends NCoreContext<SnackBarContextType, ConfigType<SnackBarContextType>> {
    constructor({
        data = []
    }: {
        data?: Array<SnackBarDataType>
    }) {
        super({
            close: () => {},
            open: () => "",
            data: data
        }, {
            key: "NCoreUIKit-SnackBarContext"
        });
    };

    useContext = (): SnackBarContextType => {
        warnSnackBarDeprecated({});

        return useContext(this.stateContext);
    };

    removeData = (id?: string) => {
        const currentData = this.state.data;

        const keyIndex = currentData.findIndex((snackBar) => snackBar.id === id);

        if(keyIndex === -1) {
            return;
        }

        currentData.splice(keyIndex, 1);

        this.setState({
            data: currentData
        });
    };

    open = (snackBarData: SnackBarDataType) => {
        const snackBarID = snackBarData.id ? snackBarData.id : uuid();

        const currentData = this.state.data;

        currentData.push({
            ...snackBarData,
            id: snackBarID
        });

        this.setState({
            data: currentData
        });

        return NCoreUIKitToast.open({
            ...getSnackBarToastProps(snackBarData),
            onClosed: (props) => {
                this.removeData(snackBarID);

                if(snackBarData.onClosed) snackBarData.onClosed(props);
            },
            id: snackBarID
        });
    };

    close = (props?: {
        index?: number;
        id?: string;
    }) => {
        warnSnackBarDeprecated({});

        const currentData = this.state.data;

        const targetId = props && props.id ? props.id : props && props.index !== undefined ? currentData[props.index]?.id : currentData[currentData.length - 1]?.id;

        if(!targetId) {
            return;
        }

        this.removeData(targetId);

        NCoreUIKitToast.close({
            id: targetId
        });
    };

    Render = ({
        children
    }: {
        children: ReactNode;
    }) => {
        return <Fragment>
            {children}
            {
                isDevelopment ?
                    <SnackBarLayerWarning/>
                :
                    null
            }
        </Fragment>;
    };
}
export default NCoreUIKitSnackBar;
